import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { fetchReleaseNotes, Spotlights } from "../queries/releaseNotes";
import useNetworkHeaders from "./useNetworkHeaders";
import useProducts from "../features/admin/employees/hooks/useProducts";

/**
 * The releases notes selector can be a CSS selector or null.
 */
type ReleasesNotesSelector = string | null;

/**
 * Methods and fields for interacting with our releases notes
 */
interface ReleaseNotesContextType {
  /**
   * Our releases notes account (gogov.releasenotes.io)
   */
  account: string;
  /**
   * Opens the release notes modal
   */
  open: (releaseId?: string) => void;
  /**
   * Closes the release notes modal
   */
  close: () => void;
  /**
   * The number of unseen release notes
   */
  unseenCount: number;
  /**
   * Removes the releases notes badge
   */
  clearBadge: () => void;
  /**
   * Returns the ID of a recent unseen spotlight release, if one exists
   */
  recentSpotlightId: string | undefined;
}

const RELEASE_NOTES_SITE = "gogov.releasenotes.io";
const RELEASE_NOTES_SELECTOR: ReleasesNotesSelector = null;
const LAST_SEEN_SPOTLIGHT_KEY = "rnw-last-seen-spotlight-date";

const ReleaseNotesContext = createContext<ReleaseNotesContextType | null>(null);

export const ReleaseNotesContextProvider = ({ children }: { children: any }) => {
  const [account] = useState(RELEASE_NOTES_SITE);
  const [selector] = useState<ReleasesNotesSelector>(RELEASE_NOTES_SELECTOR);
  const [unseenCount, setUnseenCount] = useState(0);

  const jwt = useRef<string | null>(null);
  const { hasCrm, hasCe, hasCommunications: hasNotify, hasPermits, isLoading: areProductsLoading } = useProducts();
  const [spotlightData, setSpotlightData] = useState<Spotlights | null>(null);
  const [recentSpotlightId, setRecentSpotlightId] = useState<string | undefined>(undefined);
  const storedRnwReleases = useRef<any[] | null>(null);

  const networkHeaders = useNetworkHeaders();

  const getJwt = (url: string) => {
    const jwtStart = url.indexOf("=") + 1;
    return url.slice(jwtStart);
  };

  useEffect(() => {
    if (!spotlightData || areProductsLoading) return;
    const productTagMap = {
      CRM: hasCrm,
      CE: hasCe,
      Notify: hasNotify,
      Permits: hasPermits,
      Platform: true,
    };

    const filteredReleases = spotlightData.filter((release) =>
      release.tags.every((tag) => !(tag in productTagMap) || productTagMap[tag as keyof typeof productTagMap]),
    );

    if (!filteredReleases.length) return;

    const lastSeenSpotlightDate = localStorage.getItem(LAST_SEEN_SPOTLIGHT_KEY);
    const unseenReleases = lastSeenSpotlightDate
      ? filteredReleases.filter((release) => new Date(release.released_at) > new Date(lastSeenSpotlightDate))
      : filteredReleases;

    if (!unseenReleases.length) return;

    const latestUnseenSpotlight = unseenReleases[0];
    localStorage.setItem(LAST_SEEN_SPOTLIGHT_KEY, new Date().toISOString());

    // Need to wait for the releases panel to initialize, watch for the panel to be added to the DOM
    if (document.querySelector("releases-panel")) return setRecentSpotlightId(latestUnseenSpotlight.id);
    const observer = new MutationObserver(() => {
      if (document.querySelector("releases-panel")) {
        setRecentSpotlightId(latestUnseenSpotlight.id);
        observer.disconnect();
      }
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [spotlightData, areProductsLoading]);

  /**
   * Opens the releases notes modal. This is actually documented on the releases notes
   * website. The modal can be manually opened by calling "window.rnw("show")".
   */
  const open = (releaseId?: string) => {
    const win = window as any;
    if (releaseId) {
      // Kinda hacky we need to force the library to load the selected release. It normally lazy loads releases
      // and only finds the specified release from its already loaded releases so if you try to open to a
      // specific release and that release isn't literally the first one, it just doesn't work. Thanks releasenotes.io!
      if (!win.rnw?.releases?.length) {
        win.rnw.releases = [];
        storedRnwReleases.current = null;
      } else {
        if (!storedRnwReleases.current) storedRnwReleases.current = [...win.rnw.releases];
      }
      win.rnw.releases = [spotlightData?.find((s) => s.id === releaseId)];
      // Reformat to get rid of the ugly title bar
      const releasePanel = document.getElementsByTagName("releases-panel")[0];
      releasePanel?.shadowRoot?.querySelector("div.title-bar")?.remove();
      releasePanel?.shadowRoot?.querySelector("div.releases")?.setAttribute("style", "margin-top: -2px;");
    } else {
      if (storedRnwReleases.current !== null) {
        win.rnw.releases = storedRnwReleases.current;
        storedRnwReleases.current = null;
      }
    }
    win.rnw?.("show", releaseId);
  };

  /**
   * Manually used to close the releases notes modal
   */
  const close = () => {
    const win = window as any;
    win.rnw?.("hide");
    if (storedRnwReleases.current === null) return;
    win.rnw.releases = storedRnwReleases.current;
    storedRnwReleases.current = null;
  };

  /**
   * Clears the red alert badge indicating how many unseen releases someone has seen.
   *
   * @details
   *
   * The releases notes script calculates the number of unseen releases based on the
   * last time someone has opened the releases notes modal. The value "rnw-last-opened"
   */
  const clearBadge = () => {
    localStorage.setItem("rnw-last-opened", new Date().getTime().toString());
    (window as any).rnw.unseen_count = 0;
    setUnseenCount(0);
  };

  /**
   * Gets the number of unseen releases.
   *
   * @details
   *
   * The release notes script keeps track of the number of unseen releases using the global
   * variable "window.rnw.unseen_count". The variable gets computed and set when the script
   * from the releases notes site is run.
   *
   * The number of unseen releases is calculated based on a variable kept in localstorage
   * called "rnw-last-opened".
   *
   * @returns the number of unseen releases
   */
  const getActualUnseenCount = () => {
    const win = window as any;
    let count = win?.rnw?.unseen_count;
    if (typeof count !== "number") {
      count = 0;
    }
    return count;
  };

  /**
   * Pulls in the releases notes script that builds the modal and sets the badges. This
   * should only be run once.
   */
  useEffect(() => {
    fetchReleaseNotes(networkHeaders)().then(({ url, spotlights }) => {
      /**
       * This is for our release notes page. It pulls in a script to initialize some stuff for the release notes badge and modal,
       * I'm pretty sure. Here's the link @see https://app.releasenotes.io/installation
       */
      (function (window: Window, document: Document, scriptId: string, scriptSource: string) {
        (window as any)["ReleaseNotesWidget"] = scriptId;
        (window as any)[scriptId] = function (...args: any[]) {
          ((window as any)[scriptId].q = (window as any)[scriptId].q || []).push(args);
        };

        const js = document.createElement("script"),
          fjs = document.getElementsByTagName("script")[0];
        js.id = scriptId;
        js.src = scriptSource;
        js.async = true;
        if (fjs.parentNode) fjs.parentNode.insertBefore(js, fjs);
      })(window, document, "rnw", "https://s3.amazonaws.com/cdn.releasenotes.io/v1/bootstrap.js");

      // Get the jwt to pass to release notes. We need to pass this to the call to 'init'
      const jwtToken = getJwt(url);

      // Initialize the release notes window
      (window as any).rnw("init", {
        account: account,
        selector: selector,
        jwt: jwtToken,
      });

      jwt.current = jwtToken;
      setSpotlightData(spotlights);

      // Set the unseen count - should be able to get it from the localstorage field
      setUnseenCount(getActualUnseenCount());
    });
  }, []);

  /**
   * The releases notes script will update the number of unseen releases at a point in
   * time we can't exactly hook into. To keep our state up-to-date, we can just poll
   * every couple seconds to check if the value has changed.
   */
  useEffect(() => {
    const POLL_INTERVAL = 2000;
    const handleId = setInterval(() => {
      setUnseenCount(getActualUnseenCount());
    }, POLL_INTERVAL);

    return () => {
      clearInterval(handleId);
    };
  });

  return (
    <ReleaseNotesContext.Provider
      value={{
        account,
        open,
        close,
        unseenCount,
        clearBadge,
        recentSpotlightId,
      }}
    >
      {children}
    </ReleaseNotesContext.Provider>
  );
};

export const useReleaseNotes = () => {
  const releaseNotes = useContext(ReleaseNotesContext);
  if (releaseNotes === null) {
    throw new Error("Not in release notes context!");
  }
  return releaseNotes;
};
